import React, { forwardRef, useState } from "react";
import { Row, Col, Typography } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import DynamicItem from "../../../components/forms/DynamicItem";
import AutoCompleteItem from "../../../components/forms/AutoCompleteItem";
import Phone from "../../../components/tables/Phone";
import Surface from "../../../components/layouts/Surface";
import FormHeader from "../../../components/forms/FormHeader";
import RelativeType from "../../../components/tables/RelativeType";
import CheckItem from "../../../components/tables/CheckItem";
import { ReactComponent as RelativesImg } from "../../../assets/no-relatives.svg";
import { BranchEmployeesSelect } from "../../../components/forms/AutoCompleteFormItem";
const { Text } = Typography;

const Relatives = forwardRef((props, ref) => {
  const { smallSpace, activePatient } = props;
  const intl = useIntl();
  const [employees, setEmployees] = useState([]);
  const [activeItem, setActiveItem] = useState(null);

  const onSubmit = (tableData, newRecord, activeKey, onChange) => {
    if (newRecord.main_contact) {
      let arr = [];
      const filtered = tableData.filter((i) => i.key !== activeKey);
      if (filtered.length !== tableData.length) {
        //edit item
        tableData.forEach((item, i) => {
          if (item.key === activeKey) {
            arr.push(newRecord);
          } else {
            arr.push({ ...item, main_contact: false });
          }
        });
      } else {
        //new item
        tableData.forEach((item, i) => {
          arr.push({ ...item, main_contact: false });
        });
        arr.push(newRecord);
      }
      onChange(arr);
    }
  };
  const onDataChanged = (data) => {
    // Check if arrays are different by comparing their contents
    const hasArrayChanged =
      data.length !== employees.length ||
      data.some((item, index) => item.id !== employees[index]?.id);

    if (hasArrayChanged) {
      setEmployees(data);
    }
  };

  const onFormChange = (all, changed, form) => {
    if (changed["relatives.employee_id"]) {
      const employee = employees.find(
        (emp) => emp.id === changed["relatives.employee_id"]
      );
      const newValues = {
        "relatives.first_name": employee.first_name,
        "relatives.last_name": employee.surename,
        "relatives.phone_number": employee?.contact_info?.phone_number || "",
        "relatives.mobile_number": employee?.contact_info?.mobile_number || "",
        "relatives.email": employee?.contact_info?.email || "",
        "relatives.city": employee?.address?.city || "",
        "relatives.employee": employee,
      };
      form.setFieldsValue(newValues);
      setActiveItem({
        ...activeItem,
        employee_id: changed["relatives.employee_id"],
        first_name: employee.first_name,
        last_name: employee.surename,
        employee,
      });
    } else if (
      changed.hasOwnProperty("relatives.employee_id") &&
      changed["relatives.employee_id"] === undefined
    ) {
      const newValues = {
        "relatives.first_name": "",
        "relatives.last_name": "",
        "relatives.phone_number": "",
        "relatives.mobile_number": "",
        "relatives.email": "",
        "relatives.city": "",
        "relatives.employee": null,
      };
      form.setFieldsValue(newValues);
      setActiveItem({
        ...activeItem,
        employee_id: null,
        employee: null,
      });
    }
  };
  const setFormActiveItem = (item) => {
    setActiveItem(item);
  };
  const branchId = activePatient?.branch;
  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      <Surface smallSpace={smallSpace}>
        <FormHeader title={<FormattedMessage id="app.patients.relatives" />} />
        <Row>
          <Col span={24}>
            <DynamicItem
              resource={"patients"}
              action={"editor"}
              emptyImg={<RelativesImg />}
              twoColumns={true}
              name="relatives"
              category="patient"
              addText={<FormattedMessage id="app.patients.addRelative" />}
              onFinish={onSubmit}
              onFormChange={onFormChange}
              setFormActiveItem={setFormActiveItem}
              tableItems={[
                {
                  title: "first_name",
                  type: "input",
                  required: true,
                  ellipsis: true,
                  disabled: activeItem?.employee_id,
                },
                {
                  title: "last_name",
                  type: "input",
                  ellipsis: true,
                  disabled: activeItem?.employee_id,
                },
                {
                  title: "relative_type_id",
                  type: "selectItem",
                  metadataName: "relative_types",
                  render: RelativeType,
                  required: true,
                },
                {
                  title: "city",
                  render: Phone,
                  formRender: (
                    <AutoCompleteItem
                      name="relatives.city"
                      category="patient.relatives"
                      city={true}
                      disabled={activeItem?.employee_id}
                    />
                  ),
                  disabled: activeItem?.employee_id,
                },
                {
                  title: "phone_number",
                  type: "phone",
                  render: Phone,
                  disabled: activeItem?.employee_id,
                },
                {
                  title: "mobile_number",
                  type: "phone",
                  render: Phone,
                  disabled: activeItem?.employee_id,
                },
                {
                  title: "email",
                  type: "input",
                  email: true,
                  disabled: activeItem?.employee_id,
                },
                {
                  title: "employee_id",
                  required: false,
                  render: (props) => {
                    return (
                      props?.record?.employee_id && (
                        <Text>{`${props?.record?.last_name} ${props?.record?.first_name}`}</Text>
                      )
                    );
                  },
                  formRender: (
                    <BranchEmployeesSelect
                      name="relatives.employee_id"
                      category="patient"
                      getEmployee={branchId !== null}
                      required={false}
                      extraParams={{
                        scopePrefix: `branches/${branchId}/`,
                        current: 1,
                        pageSize: 100,
                        filter: {},
                      }}
                      placeholder={intl.formatMessage({
                        id: "app.forms.patient.relatives.employee_placeholder",
                      })}
                      withId
                      items={
                        activeItem?.employee_id
                          ? [
                              {
                                id: activeItem?.employee_id,
                                first_name: activeItem?.first_name,
                                surename: activeItem?.last_name,
                              },
                            ]
                          : []
                      }
                      onDataChanged={onDataChanged}
                    />
                  ),
                },
                {
                  title: "approve_email",
                  type: "checkbox",
                  render: CheckItem,
                },
                {
                  title: "main_contact",
                  type: "checkbox",
                  render: CheckItem,
                },
              ]}
            />
          </Col>
        </Row>
      </Surface>
    </div>
  );
});

export default Relatives;
